import {
  CUSTOMER_SERVICE_DEFINITIONS,
  customerServiceDefinition,
  customerServiceForCoreSlug,
  customerServicesForPorabzarPageKind,
  isCustomerServiceSlug,
  type CustomerServiceDefinition,
  type CustomerServiceSlug,
} from '../contracts/customer-services.js';
import type { PorabzarPageKind } from '../contracts/services.js';
import {
  normalizeConfig,
  type CustomerClientConfig,
  type ResolvedCustomerClientConfig,
} from './config.js';
import { CustomerAuth } from './auth.js';
import { CustomerRequests } from './requests.js';
import { CustomerPayments } from './payments.js';
import { HttpTransport } from './http.js';

/**
 * `TamoomehClient` — the one object a host page or widget needs.
 *
 *   const client = createTamoomehClient({ apiBaseUrl: '…', authMode: 'same-site' });
 *   await client.auth.refresh();
 *   if (!client.auth.isAuthenticated) …
 *   await client.requests.create({ service: 'vakil', subject, details });
 *
 * Three surfaces, no service-specific code:
 *   - `client.auth`     — customer identity and the phone/OTP flow
 *   - `client.requests` — validated request creation and listing
 *   - `client.payments` — starting and resuming payment for a request
 *   - `client.services` — the shared service catalog
 */

/** Read-only view of the shared service catalog, scoped to this deployment. */
export class ServiceCatalog {
  constructor(private readonly config: ResolvedCustomerClientConfig) {}

  /** Every section this deployment exposes, in catalog order. */
  all(): readonly CustomerServiceDefinition[] {
    return CUSTOMER_SERVICE_DEFINITIONS.filter((definition) =>
      this.config.services.includes(definition.slug),
    );
  }

  /** One section by key, or null when unknown or not enabled here. */
  get(slug: string | null | undefined): CustomerServiceDefinition | null {
    if (!isCustomerServiceSlug(slug)) return null;
    if (!this.config.services.includes(slug)) return null;
    return customerServiceDefinition(slug);
  }

  /** The section fulfilled by a Tamoomeh Core domain service, if this deployment has one. */
  forCoreSlug(coreSlug: string | null | undefined): CustomerServiceDefinition | null {
    const definition = customerServiceForCoreSlug(coreSlug);
    if (!definition || !this.config.services.includes(definition.slug)) return null;
    return definition;
  }

  /** Sections to offer on a Porabzar content page of a given kind. */
  forPageKind(kind: PorabzarPageKind | null | undefined): readonly CustomerServiceDefinition[] {
    return customerServicesForPorabzarPageKind(kind).filter((definition) =>
      this.config.services.includes(definition.slug),
    );
  }

  isEnabled(slug: string | null | undefined): slug is CustomerServiceSlug {
    return isCustomerServiceSlug(slug) && this.config.services.includes(slug);
  }
}

export class TamoomehClient {
  readonly config: ResolvedCustomerClientConfig;
  readonly auth: CustomerAuth;
  readonly requests: CustomerRequests;
  readonly payments: CustomerPayments;
  readonly services: ServiceCatalog;
  private readonly transport: HttpTransport;

  constructor(config: CustomerClientConfig) {
    this.config = normalizeConfig(config);
    this.transport = new HttpTransport(this.config);
    this.auth = new CustomerAuth(this.config, this.transport);
    this.requests = new CustomerRequests(this.config, this.transport);
    this.payments = new CustomerPayments(this.config, this.transport);
    this.services = new ServiceCatalog(this.config);
    this.config.onEvent?.({ name: 'config.resolved', detail: { authMode: this.config.authMode } });
  }
}

export function createTamoomehClient(config: CustomerClientConfig): TamoomehClient {
  return new TamoomehClient(config);
}

export type { CustomerServiceDefinition, CustomerServiceSlug, PorabzarPageKind };
